import type { Metadata } from "next";

import type { CatalogPiece, CatalogPieceDetail } from "@/lib/api/api-client";
import { libraryPiecePath } from "@/lib/library/library-routes";
import { pieceDescription } from "@/lib/library/piece-description";
import { pieceDisplayArtist } from "@/lib/library/piece-format";

/**
 * Le titre et la description d'une fiche de morceau.
 *
 * La description est composée par `piece-description.ts`, qui explique la
 * règle. Le titre, lui, tient en une ligne : le morceau, puis qui l'a écrit.
 */
export function pieceMetadata(
  detail: CatalogPieceDetail | null,
  ref: string,
): Metadata {
  if (detail === null) {
    return {
      title: "Morceau introuvable",
      robots: { index: false, follow: true },
    };
  }

  const { piece } = detail;
  const title = pieceTitleLine(piece);
  // Composée une fois : la règle parcourt une quinzaine de bandes et sert deux
  // balises.
  const description = pieceDescription(detail);
  // Le slug résolu, pas le segment reçu : une fiche ouverte par son ancienne
  // adresse en UUID doit se déclarer sous la nouvelle, sinon la redirection
  // permanente pousse d'un côté et le canonique de l'autre.
  const canonical = libraryPiecePath({ id: ref, slug: piece.slug });

  return {
    title,
    description,
    alternates: { canonical },
    openGraph: {
      title,
      description,
      type: "music.song",
      url: canonical,
    },
  };
}

/**
 * Le morceau, puis qui l'a écrit.
 *
 * Extrait parce que deux surfaces le composent : `generateMetadata` au rendu
 * serveur, et la fiche elle-même quand c'est le client qui a trouvé la pièce
 * (voir `PieceSheet`). Virgule et non tiret : le gabarit du layout racine
 * ajoute déjà « · Virtuosa ».
 */
export function pieceTitleLine(piece: CatalogPiece): string {
  const artist = pieceDisplayArtist(piece);

  return artist === "" ? piece.title : `${piece.title}, ${artist}`;
}
